import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { LocaleEnum } from '@systemeio/ui-frontend'
import { useRouter } from 'next/router'
import { appendScript } from 'shared/utils/append-script'
import useCaptchaToken from 'modules/authentication/register/hooks/use-captcha-token'

interface RecaptchaProps {
  onTokenChange: (token: string) => void
  onError?: () => void
}

export interface RecaptchaHandle {
  reset: () => void
}

const localeMapping: Record<string, string> = {
  ua: 'uk',
  dk: 'da',
  jp: 'ja',
}

const getGoogleLocale = (locale?: string) =>
  !!locale ? (localeMapping[locale] ?? locale) : LocaleEnum.ENGLISH

export const Recaptcha = forwardRef<RecaptchaHandle, RecaptchaProps>(
  ({ onTokenChange, onError }, ref) => {
    const { locale } = useRouter()
    const recaptchaRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<number | null>(null)
    const { data: siteKey } = useCaptchaToken({ onError })

    const captchaKey = siteKey?.openKey as string

    const reset = useCallback(() => {
      onTokenChange('')
      if (widgetIdRef.current !== null) {
        window.grecaptcha?.reset(widgetIdRef.current)
      }
    }, [onTokenChange])

    useImperativeHandle(ref, () => ({ reset }), [reset])

    const loadRecaptcha = useCallback(async (captchaKey: string) => {
      const checkGrecaptcha = async () =>
        await new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            if (typeof window.grecaptcha?.render === 'function') {
              clearInterval(interval)
              resolve(void 0)
            }
          }, 100)

          setTimeout(() => {
            clearInterval(interval)
            reject()
          }, 10000)
        })

      await checkGrecaptcha()

      const shouldRender =
        recaptchaRef.current &&
        !recaptchaRef.current.hasAttribute('data-rendered') &&
        typeof window.grecaptcha?.render === 'function'

      if (shouldRender) {
        widgetIdRef.current = window.grecaptcha!.render(recaptchaRef.current, {
          sitekey: captchaKey,
          callback: (token: string) => onTokenChange(token),
          size: 'normal',
          hl: getGoogleLocale(locale),
          'error-callback': onError,
          'expired-callback': reset,
        })
        recaptchaRef.current.setAttribute('data-rendered', 'true')
      }
    }, [])

    useEffect(() => {
      if (!captchaKey) return

      appendScript(
        `https://www.google.com/recaptcha/api.js?render=explicit&hl=${getGoogleLocale(locale)}`,
      ).then(() => loadRecaptcha(captchaKey))
    }, [captchaKey])

    return <div ref={recaptchaRef} className="w-full max-w-md" />
  },
)

Recaptcha.displayName = 'Recaptcha'
